// Global Instructions Rule Applied!
import { buildFullUrl, getBackendBaseUrl, API_ROUTES } from '../../../core/utils/endpoints.ts'
import { filenameFromContentDisposition } from '../../../core/utils/contentDisposition.ts'
import {
  AnySavedCustomizationSchema,
  CustomizationContextSchema
} from '../../../state_manager/data_models/document_report_customization_model.ts'
import type {
  AnySavedCustomization,
  CustomizationContext,
  CustomizationData,
  FeedbackCustomizationData
} from '../../../state_manager/data_models/document_report_customization_model.ts'
import {
  validateReportHighlightsStatus,
  type ReportHighlightsStatus
} from '../../../state_manager/data_models/period_highlights_model.ts'
import { validateReportDescriptionsStatus } from '../../../state_manager/data_models/company_short_description.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface DocumentReportRequest {
  reportCode: string
  params: Record<string, unknown>
}

export interface PreviewResult {
  success: boolean
  /** Object URL for a text/html Blob — assign to an iframe src, then revoke. */
  blobUrl?: string
  pageCount?: number
  /** Whether the period highlights the report carries were reviewed and
   *  signed off (the X-Document-Report-Highlights header). */
  highlights?: ReportHighlightsStatus
  /** The companies whose agent-written short description the report prints
   *  without a sign-off (the X-Document-Report-Descriptions header). */
  unreviewedDescriptions?: string[]
  error?: string
}

/** The X-Document-Report-Highlights header, validated; a missing or
 *  malformed header reads as "none" rather than hiding the indicator. */
function highlightsFrom(response: Response): ReportHighlightsStatus {
  const raw = response.headers.get('X-Document-Report-Highlights')
  if (!raw) return { status: 'none' }
  try {
    return validateReportHighlightsStatus(JSON.parse(raw))
  } catch {
    return { status: 'none' }
  }
}

/** The X-Document-Report-Descriptions header, validated; missing or
 *  malformed reads as none. */
function unreviewedDescriptionsFrom(response: Response): string[] {
  const raw = response.headers.get('X-Document-Report-Descriptions')
  if (!raw) return []
  try {
    return validateReportDescriptionsStatus(JSON.parse(raw)).unreviewed
  } catch {
    return []
  }
}

/** A generated PDF held in memory, before anything is saved to disk. */
export interface PdfBlobResult {
  success: boolean
  blob?: Blob
  filename?: string
  error?: string
}

function requestBody({ reportCode, params }: DocumentReportRequest): string {
  return JSON.stringify({ data: { report_code: reportCode, params } })
}

function authHeaders(accessToken: string | null): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
  }
}

async function errorFrom(response: Response): Promise<string> {
  const body = await response.json().catch(() => ({}))
  return (body as { message?: string }).message || response.statusText
}


/**
 * Fetch a report's preview HTML and hand back an object URL for an iframe.
 *
 * The document is fetched rather than pointed at directly because an iframe
 * `src` cannot carry an Authorization header — the same reason FileViewer
 * blob-fetches PDFs. The rendered HTML is fully self-contained (fonts, CSS and
 * images inlined), so it renders correctly from a blob URL's opaque origin.
 *
 * The caller owns the returned URL and must revoke it.
 */
export async function fetchReportPreview(
  request: DocumentReportRequest,
  accessToken: string | null
): Promise<PreviewResult> {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DOCUMENT_REPORTS.PREVIEW),
      { method: 'POST', headers: authHeaders(accessToken), body: requestBody(request) }
    )

    if (!response.ok) {
      return { success: false, error: await errorFrom(response) }
    }

    const pageCount = Number(response.headers.get('X-Document-Report-Pages')) || undefined
    const highlights = highlightsFrom(response)
    const unreviewedDescriptions = unreviewedDescriptionsFrom(response)
    const blob = await response.blob()
    return { success: true, blobUrl: URL.createObjectURL(blob), pageCount, highlights, unreviewedDescriptions }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Preview failed' }
  }
}

/**
 * Generate one report's PDF and hand it back in memory.
 *
 * Separate from saving so a multi-report run can collect the PDFs and deliver
 * them as a single archive — a browser will not reliably save N files from one
 * gesture, and asking it to is what triggers Chrome's "Download multiple
 * files?" prompt and Safari's outright block.
 */
export async function fetchReportPdf(
  request: DocumentReportRequest,
  accessToken: string | null
): Promise<PdfBlobResult> {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DOCUMENT_REPORTS.PDF),
      { method: 'POST', headers: authHeaders(accessToken), body: requestBody(request) }
    )

    if (!response.ok) {
      return { success: false, error: await errorFrom(response) }
    }

    const blob = await response.blob()
    // Readable cross-origin only because the route sends
    // Access-Control-Expose-Headers; without it this silently falls back.
    const filename = filenameFromContentDisposition(
      response.headers.get('Content-Disposition'),
      'report.pdf'
    )
    return { success: true, blob, filename }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'PDF download failed' }
  }
}

export interface CustomizationContextResult {
  success: boolean
  context?: CustomizationContext
  error?: string
}

/**
 * Load everything the Customize drawer needs in one call: the generated
 * content, the key-driver pool, the saved edits, char budgets and the
 * resolved period the save keys on. Validated with Zod — a shape mismatch is
 * an error, never coerced.
 */
export async function fetchCustomizationContext(
  request: DocumentReportRequest,
  accessToken: string | null
): Promise<CustomizationContextResult> {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DOCUMENT_REPORTS.CUSTOMIZATION_CONTEXT),
      { method: 'POST', headers: authHeaders(accessToken), body: requestBody(request) }
    )
    if (!response.ok) {
      return { success: false, error: await errorFrom(response) }
    }
    const body = await response.json()
    return { success: true, context: CustomizationContextSchema.parse(body.data) }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed to load customization' }
  }
}

export interface SaveCustomizationResult {
  success: boolean
  /** The saved row, or null when the save cleared everything back to generated. */
  saved?: AnySavedCustomization | null
  error?: string
}

/**
 * Save (or, with an all-auto payload, clear) one report customization.
 *
 * The SUBJECT is exactly one of `investorAccountId` (performance report) or
 * `fundId` (feedback report — a fund or consolidated-fund id; the backend
 * resolves which). The drawer passes whichever its context variant carries.
 */
export async function saveReportCustomization(
  payload: {
    reportCode: string
    investorAccountId?: string
    fundId?: string
    periodStart: string
    periodEnd: string
    data: CustomizationData | FeedbackCustomizationData
  },
  accessToken: string | null
): Promise<SaveCustomizationResult> {
  try {
    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.DOCUMENT_REPORTS.CUSTOMIZATIONS),
      {
        method: 'PUT',
        headers: authHeaders(accessToken),
        body: JSON.stringify({
          data: {
            report_code: payload.reportCode,
            ...(payload.investorAccountId
              ? { investor_account_id: payload.investorAccountId }
              : {}),
            ...(payload.fundId ? { fund_id: payload.fundId } : {}),
            period_start: payload.periodStart,
            period_end: payload.periodEnd,
            data: payload.data
          }
        })
      }
    )
    if (!response.ok) {
      return { success: false, error: await errorFrom(response) }
    }
    const body = await response.json()
    const saved = body.data == null ? null : AnySavedCustomizationSchema.parse(body.data)
    return { success: true, saved }
  } catch (e) {
    return { success: false, error: e instanceof Error ? e.message : 'Failed to save customization' }
  }
}

/**
 * Save a blob to the user's disk — the house blob-download pattern
 * (see exportJsonToExcel in core/services/utilitiesService.ts).
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

